/**
 * ActionArea -- Workspace conversacional adaptativo para #/agents.
 *
 * Container persistente que cambia su contenido segun metadata que devuelve Argemiro
 * (target_service, next_action). Inspirado en legacy Nexo Web microsite/argemiro pero
 * implementado moderno: factory + closures + state machine vanilla, cero global state.
 *
 * Coexiste con chat tradicional: cuando esta en modo 'chat' (default), se mantiene
 * collapsed (0px) y la vista #/agents es chat puro. Cuando Argemiro invoca un modo
 * (via metadata en la respuesta n8n), el container se expande con el contenido.
 *
 * Modos top-level disponibles via showMode():
 *   - chat              : passthrough (collapsed, no renderiza nada)
 *   - inline-upload     : upload de archivo unico con preview + onSend callback
 *   - table-results     : tabla de filas + headers + onRowClick
 *   - image-response    : imagen unica con acciones download/fullscreen/reset
 *   - stepper-flow      : meta-modo wizard N pasos con multimedia embebida (T2 v1.1+)
 *   - match-review      : revision de matcheo de factura OCR (Compras)
 *   - reconteo-decisions: revision de discrepancias de conteo de inventario (Argemiro Reconteo)
 *   - argemiro-propuestas: el SI del dueno sobre lo que Argemiro propone (CC-BOTON-T2)
 *
 * Sub-modos (solo embebibles dentro de stepper-flow, NO invocables top-level):
 *   - text-image      : markdown + img (informativo)
 *   - video           : embed YT / MP4 con aspect 16:9
 *   - quiz-embedded   : pregunta + opciones + feedback inmediato
 *
 * Uso:
 *   import { createActionArea } from '../components/ActionArea.js';
 *   const actionArea = createActionArea(document.getElementById('nx-action-area'));
 *   actionArea.showMode('inline-upload', { label: 'Sube factura', onSend: (files) => {...} });
 *   actionArea.showMode('stepper-flow', { title:'Onboarding', steps:[...], onComplete:fn });
 *   actionArea.clear();  // Volver a chat (collapsed)
 *
 * Eventos (suscribibles via .on(event, fn)):
 *   - 'mode-change'   : { from, to, payload }
 *   - 'mode-action'   : { mode, action, data }   (ej. upload-send, table-row-click, stepper step-change/completed/exited)
 *   - 'cleared'       : (sin payload)
 *
 * @module presentation/components/ActionArea
 */

import { renderStepperFlow } from './ActionAreaModes/stepper-flow.js';
import { renderMatchReview } from './ActionAreaModes/match-review.js';
import { renderReconteoDecisions } from './ActionAreaModes/reconteo-decisions.js';
import { renderArgemiroPropuestas } from './ActionAreaModes/argemiro-propuestas.js';

const VERSION = 'v1.1';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escHtml } from '../shared/texto.js';
/**
 * Crea una instancia ActionArea atada a un container DOM.
 * @param {HTMLElement} container - elemento DOM donde se monta la action area
 * @param {object} [opts]
 * @param {boolean} [opts.debug] - si true, expone instancia en window.__nexo.actionArea para inspeccion
 * @returns {object} instancia con API publica { showMode, clear, getState, on, off, version }
 */
export function createActionArea(container, opts = {}) {
  if (!container) throw new Error('ActionArea: container is required');

  // Estado privado encapsulado en closure
  const state = {
    mode: 'chat',
    payload: null,
    history: [],
    listeners: new Map() // event -> Set<callback>
  };

  // Container styling base (collapsed por defecto)
  container.classList.add('nx-action-area');
  applyContainerBaseStyles(container);

  // Render inicial: chat passthrough (collapsed, vacio)
  renderChat(container);

  // ============== API publica ==============

  /**
   * Cambia el modo activo + renderiza contenido. Emite 'mode-change'.
   * @param {string} modeName - nombre del modo (ver MODES abajo)
   * @param {object} payload - data especifica del modo
   */
  function showMode(modeName, payload = {}) {
    const handler = MODES[modeName];
    if (!handler) {
      console.warn(`[ActionArea] Modo desconocido: ${modeName}. Modos validos: ${Object.keys(MODES).join(', ')}`);
      return;
    }

    const previousMode = state.mode;
    state.history.push({ mode: previousMode, payload: state.payload, timestamp: Date.now() });
    state.mode = modeName;
    state.payload = payload;

    cleanupContainer(container);
    handler(container, payload, buildContext());

    // Toggle collapsed/expanded class para CSS animaciones
    container.classList.toggle('nx-action-area--active', modeName !== 'chat');
    container.dataset.mode = modeName;

    emit('mode-change', { from: previousMode, to: modeName, payload });
  }

  /**
   * Vuelve al modo chat (collapsed). Equivalente a showMode('chat').
   */
  function clear() {
    showMode('chat', null);
    emit('cleared');
  }

  /**
   * Snapshot del estado actual. Read-only.
   */
  function getState() {
    return {
      mode: state.mode,
      payload: state.payload ? { ...state.payload } : null,
      historyLength: state.history.length
    };
  }

  /**
   * Suscribe callback a un evento.
   * @returns {function} unsubscribe
   */
  function on(event, callback) {
    if (!state.listeners.has(event)) state.listeners.set(event, new Set());
    state.listeners.get(event).add(callback);
    return () => off(event, callback);
  }

  function off(event, callback) {
    state.listeners.get(event)?.delete(callback);
  }

  function emit(event, data) {
    state.listeners.get(event)?.forEach(cb => {
      try { cb(data); }
      catch (e) { console.error(`[ActionArea] listener ${event} threw:`, e); }
    });
  }

  // Context que reciben los handlers de cada modo
  function buildContext() {
    return {
      clear,
      showMode,
      emit,
      escHtml
    };
  }

  const instance = {
    showMode,
    clear,
    getState,
    on,
    off,
    version: VERSION
  };

  if (opts.debug) {
    window.__nexo = window.__nexo || {};
    window.__nexo.actionArea = instance;
  }

  return instance;
}

// ============== Container styling ==============

function applyContainerBaseStyles(container) {
  // Base: collapsed when mode is 'chat'. Expands cuando se activa otro modo.
  // Animacion suave con max-height + opacity.
  Object.assign(container.style, {
    width: '100%',
    maxHeight: '0',
    overflow: 'hidden',
    transition: 'max-height 0.25s ease-out, opacity 0.2s, margin 0.2s',
    opacity: '0',
    background: '#0d1117',
    border: '1px solid transparent',
    borderRadius: '8px',
    margin: '0',
    boxSizing: 'border-box'
  });
}

function cleanupContainer(container) {
  // Limpia event listeners ligados a hijos antes de innerHTML reset
  container.replaceChildren();
}

// ============== Modos ==============

const MODES = {
  'chat':                renderChat,
  'inline-upload':       renderInlineUpload,
  'table-results':       renderTableResults,
  'image-response':      renderImageResponse,
  'stepper-flow':        renderStepperFlow,
  'match-review':        renderMatchReview,
  'reconteo-decisions':  renderReconteoDecisions,
  'argemiro-propuestas': renderArgemiroPropuestas
};

/**
 * Modo chat: passthrough. No renderiza nada. Container collapsa via CSS.
 */
function renderChat(container) {
  Object.assign(container.style, {
    maxHeight: '0',
    opacity: '0',
    margin: '0',
    border: '1px solid transparent',
    padding: '0'
  });
  // Sin innerHTML — el chat real vive fuera de la action-area
}

/**
 * Modo inline-upload: upload de archivo(s) + preview + boton enviar.
 * payload: { label?, accept?, maxSize?, multiple?, onSend(files) }
 */
function renderInlineUpload(container, payload, ctx) {
  expandContainer(container, '#1f6feb');

  const label = payload?.label || 'Subir archivo';
  const accept = payload?.accept || 'image/png,image/jpeg,image/webp,application/pdf';
  const maxSize = payload?.maxSize || 5 * 1024 * 1024; // 5MB
  const multiple = !!payload?.multiple;

  container.innerHTML = `
    <div style="padding:16px;display:flex;flex-direction:column;gap:12px;">
      <header style="display:flex;align-items:center;justify-content:space-between;gap:8px">
        <h3 style="margin:0;font-size:0.95rem;color:#f0f6fc;">📤 ${ctx.escHtml(label)}</h3>
        <button data-aa-close type="button"
                style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.1rem;padding:0 4px;"
                title="Cerrar">×</button>
      </header>
      <input type="file" data-aa-upload-input accept="${ctx.escHtml(accept)}"${multiple ? ' multiple' : ''}
             style="padding:8px;background:#0d1117;border:1px dashed #30363d;border-radius:6px;color:#c9d1d9;font-size:0.875rem;cursor:pointer"/>
      <div data-aa-upload-preview style="display:none;flex-direction:column;gap:8px"></div>
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <button data-aa-cancel type="button"
                style="padding:6px 14px;background:transparent;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:pointer;font-size:0.875rem">Cancelar</button>
        <button data-aa-send type="button" disabled
                style="padding:6px 14px;background:#238636;border:1px solid #2ea043;border-radius:5px;color:#fff;cursor:pointer;font-size:0.875rem;opacity:0.5">Enviar</button>
      </div>
    </div>
  `;

  const $input = container.querySelector('[data-aa-upload-input]');
  const $preview = container.querySelector('[data-aa-upload-preview]');
  const $send = container.querySelector('[data-aa-send]');
  const $cancel = container.querySelector('[data-aa-cancel]');
  const $close = container.querySelector('[data-aa-close]');

  let selectedFiles = [];

  $input.addEventListener('change', (e) => {
    selectedFiles = Array.from(e.target.files || []).filter(f => {
      if (f.size > maxSize) {
        console.warn(`[ActionArea] Archivo ${f.name} excede ${(maxSize / 1024 / 1024).toFixed(1)}MB. Omitido.`);
        return false;
      }
      return true;
    });
    renderPreview();
    $send.disabled = selectedFiles.length === 0;
    $send.style.opacity = selectedFiles.length === 0 ? '0.5' : '1';
  });

  function renderPreview() {
    if (selectedFiles.length === 0) {
      $preview.style.display = 'none';
      $preview.innerHTML = '';
      return;
    }
    $preview.style.display = 'flex';
    $preview.innerHTML = selectedFiles.map((f, i) => {
      const isImg = f.type.startsWith('image/');
      const url = isImg ? URL.createObjectURL(f) : '';
      return `
        <div style="display:flex;gap:10px;align-items:center;padding:6px 10px;background:#0d1117;border:1px solid #30363d;border-radius:5px">
          ${isImg
            ? `<img src="${url}" alt="" style="width:40px;height:40px;object-fit:cover;border-radius:4px"/>`
            : `<span style="font-size:1.3rem">📄</span>`}
          <div style="flex:1;min-width:0;font-size:0.875rem">
            <div style="color:#c9d1d9;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${ctx.escHtml(f.name)}</div>
            <div style="color:#8b949e;font-size:0.875rem">${(f.size / 1024).toFixed(1)} KB</div>
          </div>
        </div>
      `;
    }).join('');
  }

  $send.addEventListener('click', () => {
    if (selectedFiles.length === 0) return;
    ctx.emit('mode-action', { mode: 'inline-upload', action: 'send', data: { files: selectedFiles } });
    if (typeof payload.onSend === 'function') {
      try { payload.onSend(selectedFiles); }
      catch (e) { console.error('[ActionArea] onSend threw:', e); }
    }
  });

  $cancel.addEventListener('click', () => ctx.clear());
  $close.addEventListener('click', () => ctx.clear());
}

/**
 * Modo table-results: tabla de filas con headers.
 * payload: { headers?, rows, title?, onRowClick(row, idx)? }
 */
function renderTableResults(container, payload, ctx) {
  expandContainer(container, '#8957e5');

  const rows = payload?.rows || [];
  const title = payload?.title || 'Resultados';
  const headers = payload?.headers || (rows[0] ? Object.keys(rows[0]) : []);

  const headerHtml = headers.map(h =>
    `<th style="padding:6px 10px;text-align:left;border-bottom:1px solid #30363d;font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.04em">${ctx.escHtml(h)}</th>`
  ).join('');

  const rowsHtml = rows.map((row, idx) => {
    const cells = headers.map(h => {
      const v = row[h];
      return `<td style="padding:6px 10px;border-bottom:1px solid #21262d;font-size:0.875rem;color:#c9d1d9">${ctx.escHtml(v ?? '-')}</td>`;
    }).join('');
    return `<tr data-aa-row-idx="${idx}" style="cursor:${payload.onRowClick ? 'pointer' : 'default'}">${cells}</tr>`;
  }).join('');

  container.innerHTML = `
    <div style="padding:16px;display:flex;flex-direction:column;gap:10px;">
      <header style="display:flex;align-items:center;justify-content:space-between;gap:8px">
        <h3 style="margin:0;font-size:0.95rem;color:#f0f6fc;">📊 ${ctx.escHtml(title)} <span style="color:#8b949e;font-weight:400;font-size:14px">(${rows.length})</span></h3>
        <button data-aa-close type="button" style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.1rem;padding:0 4px" title="Cerrar">×</button>
      </header>
      <div style="overflow-x:auto;max-height:340px;overflow-y:auto;border:1px solid #30363d;border-radius:6px;background:#0d1117">
        <table style="border-collapse:collapse;width:100%">
          <thead style="position:sticky;top:0;background:#161b22;z-index:1"><tr>${headerHtml}</tr></thead>
          <tbody>${rowsHtml || `<tr><td colspan="${headers.length}" style="padding:14px;text-align:center;color:#8b949e;font-size:0.875rem">Sin filas</td></tr>`}</tbody>
        </table>
      </div>
    </div>
  `;

  if (payload.onRowClick) {
    container.querySelectorAll('[data-aa-row-idx]').forEach(tr => {
      tr.addEventListener('mouseover', () => tr.style.background = '#1f2733');
      tr.addEventListener('mouseout', () => tr.style.background = '');
      tr.addEventListener('click', () => {
        const idx = parseInt(tr.dataset.aaRowIdx, 10);
        const row = rows[idx];
        ctx.emit('mode-action', { mode: 'table-results', action: 'row-click', data: { row, idx } });
        try { payload.onRowClick(row, idx); }
        catch (e) { console.error('[ActionArea] onRowClick threw:', e); }
      });
    });
  }

  container.querySelector('[data-aa-close]').addEventListener('click', () => ctx.clear());
}

/**
 * Modo image-response: imagen grande + acciones (download, fullscreen, reset).
 * payload: { src, alt?, title?, fileName?, actions? }
 *   actions = ['download', 'fullscreen', 'reset'] (default: las 3)
 */
function renderImageResponse(container, payload, ctx) {
  expandContainer(container, '#3fb950');

  const src = payload?.src || '';
  const alt = payload?.alt || 'Resultado';
  const title = payload?.title || 'Imagen generada';
  const fileName = payload?.fileName || 'resultado.png';
  const actions = payload?.actions || ['download', 'fullscreen', 'reset'];

  if (!src) {
    container.innerHTML = `<div style="padding:16px;color:#f85149;font-size:0.875rem">image-response sin src</div>`;
    return;
  }

  const btnStyle = 'padding:6px 12px;border:1px solid #30363d;border-radius:5px;background:#21262d;color:#c9d1d9;cursor:pointer;font-size:0.875rem;display:inline-flex;align-items:center;gap:6px';

  const actionBtns = [];
  if (actions.includes('download')) actionBtns.push(`<button data-aa-download type="button" style="${btnStyle}">⬇ Descargar</button>`);
  if (actions.includes('fullscreen')) actionBtns.push(`<button data-aa-fullscreen type="button" style="${btnStyle}">⛶ Ampliar</button>`);
  if (actions.includes('reset')) actionBtns.push(`<button data-aa-reset type="button" style="${btnStyle}">↺ Reiniciar</button>`);

  container.innerHTML = `
    <div style="padding:16px;display:flex;flex-direction:column;gap:12px;">
      <header style="display:flex;align-items:center;justify-content:space-between;gap:8px">
        <h3 style="margin:0;font-size:0.95rem;color:#f0f6fc;">🖼 ${ctx.escHtml(title)}</h3>
        <button data-aa-close type="button" style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.1rem;padding:0 4px" title="Cerrar">×</button>
      </header>
      <div style="display:flex;justify-content:center;background:#0d1117;border:1px solid #30363d;border-radius:6px;padding:12px">
        <img src="${ctx.escHtml(src)}" alt="${ctx.escHtml(alt)}" style="max-width:100%;max-height:380px;object-fit:contain;border-radius:4px"/>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap">${actionBtns.join('')}</div>
    </div>
  `;

  const $download = container.querySelector('[data-aa-download]');
  const $fullscreen = container.querySelector('[data-aa-fullscreen]');
  const $reset = container.querySelector('[data-aa-reset]');
  const $close = container.querySelector('[data-aa-close]');

  $download?.addEventListener('click', () => {
    const a = document.createElement('a');
    a.href = src;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 200);
    ctx.emit('mode-action', { mode: 'image-response', action: 'download', data: { fileName } });
  });

  $fullscreen?.addEventListener('click', () => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.92);z-index:2000;display:flex;align-items:center;justify-content:center;padding:24px;cursor:zoom-out';
    overlay.innerHTML = `<img src="${escHtml(src)}" alt="${escHtml(alt)}" style="max-width:95vw;max-height:90vh;object-fit:contain"/>`;
    overlay.addEventListener('click', () => overlay.remove());
    document.body.appendChild(overlay);
    ctx.emit('mode-action', { mode: 'image-response', action: 'fullscreen', data: {} });
  });

  $reset?.addEventListener('click', () => {
    ctx.emit('mode-action', { mode: 'image-response', action: 'reset', data: {} });
    if (typeof payload.onReset === 'function') {
      try { payload.onReset(); }
      catch (e) { console.error('[ActionArea] onReset threw:', e); }
    }
    ctx.clear();
  });

  $close.addEventListener('click', () => ctx.clear());
}

/**
 * Helper: expande el container con animacion + color de borde segun el modo.
 */
function expandContainer(container, accentColor) {
  Object.assign(container.style, {
    maxHeight: '480px',
    opacity: '1',
    margin: '12px 0',
    border: `1px solid ${accentColor}`,
    padding: '0'
  });
}

export const ACTION_AREA_VERSION = VERSION;
